import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components/macro';
import {
  colors,
  Icons,
  Button,
  Tooltip,
  ToggleSwitch,
  Spinner,
} from '@united-talent-agency/components';
import StatusList from '../StatusList';
import { HEADER } from '../../support/cypressTags';
import { notify } from 'react-notify-toast';
import {
  useCallRecordsStatusCheckQuery,
  useDeleteStatusMutation,
  useUpdateDeskMutation,
  useUpdateStatusMutation,
  DeskDocument,
  DeskStatusesDocument,
  CallRecordsDocument,
} from '../../generated/graphql';

export const updateStatusOrder = ({ oldIndex, newIndex }, statuses, onChange) => {
  let _statuses = statuses.slice();
  //switch the status item's places in the array
  const movedStatusItem = _statuses.splice(oldIndex, 1);
  _statuses.splice(newIndex, 0, ...movedStatusItem);

  //set the order field to be the index
  for (let i = 0; i < _statuses.length; i++) {
    _statuses[i].order = i;
  }

  onChange(_statuses);
  return _statuses;
};

export const SettingsModalContent = ({ desk, totalCounts, deskStatuses, onCancel, onSave }) => {
  // Mutation
  const [updateDesk] = useUpdateDeskMutation({
    refetchQueries: [DeskDocument, DeskStatusesDocument, CallRecordsDocument],
    awaitRefetchQueries: true,
  });
  const [updateStatuses] = useUpdateStatusMutation({
    refetchQueries: [DeskStatusesDocument, CallRecordsDocument],
    awaitRefetchQueries: true,
  });
  const [deleteStatuses] = useDeleteStatusMutation({
    refetchQueries: [DeskStatusesDocument, CallRecordsDocument],
    awaitRefetchQueries: true,
  });
  const { refetch: refetchCallRecordsStatusCheck } = useCallRecordsStatusCheckQuery({
    skip: true,
  });

  // States
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState();
  const [settingsErrorState, setSettingsErrorState] = useState(false);
  const [hideFutureCallsChecked, setHideFutureCallsChecked] = useState(
    !!desk?.settings?.hideFutureCalls
  );
  const [collapseCallFormChecked, setCollapseCallFormChecked] = useState(
    !!desk?.settings?.collapseCallForm
  );
  const [hideBulkEditsChecked, setHideBulkEditsChecked] = useState(!!desk?.settings?.hideBulkEdits);

  const todayDisplayText = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });

  const fetchStatuses = async () => {
    // Creates a shallow copy of each object in the array to prevent modifying the original array.
    const deskStatusesCopy = deskStatuses.map((item) => ({ ...item }));
    const { data } = await refetchCallRecordsStatusCheck({
      deskId: desk._id,
      statuses: deskStatusesCopy.map((status) => status.status),
    });

    const statusChecks = data?.callRecordsStatusCheck ?? [];

    const updatedStatuses = deskStatusesCopy.map((status) => {
      const matchingStatus = statusChecks.find((check) => check.status === status.status);

      return {
        ...status,
        hasCallRecord: !!matchingStatus?.hasCallRecord,
      };
    });

    setStatuses(updatedStatuses);
  };

  useEffect(() => {
    fetchStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deskStatuses]);

  useEffect(() => {
    setHideBulkEditsChecked(!!desk?.settings?.hideBulkEdits);
  }, [desk?.settings?.hideBulkEdits]);

  useEffect(() => {
    setHideFutureCallsChecked(!!desk?.settings?.hideFutureCalls);
  }, [desk?.settings?.hideFutureCalls]);

  useEffect(() => {
    setCollapseCallFormChecked(!!desk?.settings?.collapseCallForm);
  }, [desk?.settings?.collapseCallForm]);

  /** Handles closing the modal
   *
   */
  const handleOnClickClose = () => {
    onCancel();
  };

  /** Removes a status from the status list
   *
   * @param {Number} index: the index of the status to remove
   **/
  const removeStatus = (index) => {
    let _statuses = statuses.slice();

    _statuses.splice(index, 1);
    setStatuses(_statuses);
  };

  /** Updates a status from the status list
   *
   * @param {("color"|"name"|"status"|"type")} fieldType: the field to update on the status object
   * @param {String} updatedValue: the new value
   * @param {Number} index: the index of the status to update
   **/
  const updateStatusField = (fieldType, updatedValue, index) => {
    let _statuses = [...statuses];
    _statuses[index][fieldType] = updatedValue;
    setStatuses(_statuses);
  };

  const settingsFieldsAreValid = useMemo(() => {
    const _statuses = [].concat.apply(
      [],
      statuses?.length ? statuses?.map((status) => Object.values(status)) : ['']
    );

    return !_statuses.includes('');
  }, [statuses]);

  /**
   * Handles saving changes to the status list
   * Saves changes, then updates the search params in the querystring
   **/
  const handleSave = async () => {
    if (loading) {
      return;
    }

    if (settingsFieldsAreValid) {
      setLoading(true);

      try {
        const statusesToCreateOrUpdate = statuses.map((status) => {
          const statusCopy = { ...status };
          delete statusCopy?.hasCallRecord;
          delete statusCopy?.count;

          return statusCopy;
        });

        const idsOfExistingStatuses = statuses.map((status) => status?._id).filter(Boolean);
        const statusesToDelete = deskStatuses
          .filter((status) => !idsOfExistingStatuses.includes(status._id))
          .map((status) => {
            const statusCopy = { ...status };
            delete statusCopy?.hasCallRecord;
            delete statusCopy?.count;

            return { ...statusCopy, remove: true };
          });

        if (statusesToDelete?.length) {
          const { data: deleteData } = await deleteStatuses({
            variables: {
              statuses: statusesToDelete,
            },
          });
          if (!deleteData?.deleteStatus?.success) {
            throw new Error(deleteData?.deleteStatus?.message || 'Failed to delete statuses');
          }
        }

        const { data: updateStatusesData } = await updateStatuses({
          variables: {
            input: statusesToCreateOrUpdate,
          },
        });
        if (!updateStatusesData?.updateStatus?.success) {
          throw new Error(updateStatusesData?.updateStatus?.message || 'Failed to update statuses');
        }

        const { data: updateDeskData } = await updateDesk({
          variables: {
            id: desk?._id,
            input: {
              settings: {
                hideFutureCalls: hideFutureCallsChecked,
                hideBulkEdits: hideBulkEditsChecked,
                collapseCallForm: collapseCallFormChecked,
              },
            },
          },
        });
        if (!updateDeskData?.updateDesk?.success) {
          throw new Error(updateDeskData?.updateDesk?.message || 'Failed to update desk');
        }

        setSettingsErrorState(false);
        onSave && onSave();
        setLoading(false);
        notify.show('Settings saved', 'success');
      } catch (error) {
        setLoading(false);
        console.error(error);
        notify.show(error.message, 'error');
      }
    } else {
      setSettingsErrorState(true);
    }
  };

  const handleBulkToggleSwitch = () => {
    setHideBulkEditsChecked((previousValue) => !previousValue);
  };

  const addStatus = useCallback(() => {
    setStatuses((oldState) => [
      ...oldState,
      {
        order: oldState.length + 1,
        color: '',
        status: '',
        name: '',
        type: '',
        count: 0,
        deskId: (desk || {})._id || '',
      },
    ]);
  }, [desk]);

  return (
    <div>
      <CloseButtonContainer data-cy={HEADER.SETTINGS.MODAL_CLOSE}>
        <Icons.XCloseIcon
          inline
          onClick={handleOnClickClose}
          height={23}
          width={23}
          style={{ cursor: 'pointer', marginRight: -7 }}
        />
      </CloseButtonContainer>

      <ModalTitle>Settings</ModalTitle>
      <div style={{ display: 'flex', margin: '20px 40px', justifyContent: 'space-between' }}>
        <span>Bulk Changes</span>
        <ToggleSwitch value={!hideBulkEditsChecked} onChange={handleBulkToggleSwitch} />
      </div>
      <div
        data-cy={HEADER.SETTINGS.MODAL_ALWAYS_KEEP_CALL_FORM_OPENED}
        style={{ display: 'flex', margin: '20px 40px', justifyContent: 'space-between' }}
      >
        <span>Always show Add Call form</span>
        <ToggleSwitch
          value={!collapseCallFormChecked}
          onChange={() => setCollapseCallFormChecked((oldState) => !oldState)}
        />
      </div>
      <hr style={{ margin: '0px 40px' }} />
      <div style={{ display: 'flex', margin: '20px 40px', justifyContent: 'space-between' }}>
        <span>Hide future calls</span>
        <Tooltip
          text={`Toggle ON: will only display Call Records up to today [${todayDisplayText}]`}
          place="right"
          width="300px"
        >
          <div
            data-cy={HEADER.SETTINGS.MODAL_HIDE_FUTURE_CALLS}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <ToggleSwitch
              value={hideFutureCallsChecked}
              onChange={() => setHideFutureCallsChecked((oldState) => !oldState)}
            />
          </div>
        </Tooltip>
      </div>
      <p style={{ fontSize: 18, margin: '0 47px -5px', fontWeight: 300 }}>Status</p>
      <p style={{ color: 'grey', marginLeft: 47, fontWeight: 300, fontSize: 12 }}>
        (Drag and Drop to reorder)
      </p>

      <StatusList
        useDragHandle
        distance={5}
        desk={desk}
        statuses={statuses}
        totalCounts={totalCounts}
        errorState={settingsErrorState}
        removeStatus={removeStatus}
        updateStatusField={updateStatusField}
        onSortEnd={(indexUpdate) => updateStatusOrder(indexUpdate, statuses, setStatuses)}
      />

      <AddStatusContainer>
        {(statuses || []).length < 6 ? (
          <AddStatusText type="button" tabIndex={0} onClick={addStatus}>
            <span style={{ fontSize: 18 }}>+</span>
            <span style={{ position: 'relative', margin: 'auto 4px' }}>Add Status</span>
          </AddStatusText>
        ) : (
          <Tooltip text="You can only have 6 statuses">
            <DisabledAddStatusText type="button" tabIndex={0}>
              <span style={{ fontSize: 18 }}>+</span>
              <span style={{ position: 'relative', margin: 'auto 4px' }}>Add Status</span>
            </DisabledAddStatusText>
          </Tooltip>
        )}
      </AddStatusContainer>

      <SaveButtonContainer data-cy={HEADER.SETTINGS.MODAL_SAVE}>
        <SaveButton
          tabIndex={0}
          small
          onClick={handleSave}
          data-loading={loading}
          text={loading ? <Spinner size={15} /> : 'Save'}
        />
      </SaveButtonContainer>
    </div>
  );
};

const AddStatusContainer = styled.div({
  margin: '5px 48px',
});

const AddStatusText = styled.button({
  display: 'flex',
  width: 'max-content',
  backgroundColor: 'transparent',
  outline: 'none',
  cursor: 'pointer',
  fontFamily: 'aktiv-grotesk, sans-serif',
  fontSize: 12,
  color: colors.linkBlue,

  border: '1px solid transparent',
  ':focus': {
    border: '1px solid rgb(33, 135, 185)',
  },
});

const DisabledAddStatusText = styled(AddStatusText)({
  color: colors.disabledGrey,
  cursor: 'default',
});

const CloseButtonContainer = styled.div({
  position: 'absolute',
  right: 15,
});

const SaveButtonContainer = styled.div({
  margin: '0 38px 12px 0',
  justifyContent: 'flex-end',
  display: 'flex',
  position: 'relative',
});

const SaveButton = styled(Button)({
  transition: 'all 350ms',
  // Focus
  ':focus': {
    border: '1px solid rgb(33, 135, 185)',
  },
  // Loading
  '&[data-loading="true"]': {
    backgroundColor: 'transparent',
  },
  '&[data-loading="true"]:hover': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '&[data-loading="true"] > span': {
    display: 'flex',
    alignItems: 'center',
  },
});

const ModalTitle = styled.div({
  display: 'flex',
  justifyContent: 'center',
  fontSize: 24,
  fontWeight: 300,
});

export default SettingsModalContent;
